import apiClient from './api';
import { PaginatedResponse } from '../types/common';
import { API_BASE_URL } from '../config/env';

// Populated Category shape (returned by list endpoint when categoryIds are populated)
export interface PopulatedCategoryRef {
  _id?: string;
  id?: string;
  name: string;
  slug: string;
  level?: 1 | 2;
  parent?: { _id?: string; id?: string; name: string; slug: string } | null;
}

// Define interface for AccountType
export interface AccountType {
  id: string;
  name: string;
  description: string;
  image?: string;
  category?: string;
  // ID strings when sent from forms; populated objects when returned by list endpoint
  categoryIds?: Array<string | PopulatedCategoryRef>;
  activityArea?: string;
  isActive: boolean;
  isDefault: boolean;
  fields: FieldDefinition[];
  createdAt: string;
  updatedAt: string;
}

// شرط نمایش فیلد
export type VisibilityOperator =
  | 'equals'
  | 'notEquals'
  | 'truthy'
  | 'falsy'
  | 'in'
  | 'notIn'
  | 'gt'
  | 'lt'
  | 'contains';

export interface VisibilityCondition {
  fieldName: string;
  operator: VisibilityOperator;
  value?: any;
}

export interface FieldVisibility {
  conditions: VisibilityCondition[];
  logic?: 'AND' | 'OR';
}

// Define interface for field types
export interface FieldDefinition {
  type:
    | 'text'
    | 'number'
    | 'email'
    | 'date'
    | 'dropdown'
    | 'checkbox'
    | 'radio'
    | 'price'
    | 'inventory'
    | 'textarea'
    | 'password'
    | 'capacity_type'
    | 'value'
    | 'credentials'
    | 'color'
    | 'player_id';
  name: string;
  label: string;
  placeholder?: string;
  description?: string;
  required: boolean;
  order: number;
  clientOnly?: boolean;
  visibility?: FieldVisibility;
  subFields?: FieldDefinition[]; // ✅ فرم تو در تو
  validation?: {
    min?: number;
    max?: number;
    regex?: string;
    errorMessage?: string;
  };
  options?: {
    isStatic?: boolean;
    items?: FieldOption[];
    dynamicSource?: DynamicSource;
    priceMatrix?: PriceOption[];
    capacityTypes?: CapacityOption[];
    perCapacityInventory?: boolean;
    credentials?: {
      usernameRequired?: boolean;
      passwordRequired?: boolean;
      descriptionRequired?: boolean;
      usernameLabel?: string;
      passwordLabel?: string;
      descriptionLabel?: string;
    };
    isMultiSelect?: boolean;
  };
}

export interface FieldOption {
  label: string;
  value: string;
}

export interface DynamicSource {
  collectionName: string;
  displayField: string;
  valueField: string;
  filters?: Record<string, any>;
  isMultiSelect?: boolean;
}

export interface PriceOption {
  capacity: number;
  price: number;
  description?: string;
}

export interface CapacityOption {
  name: string; // نام حجم/سایز (مثل "۵۰ میلی‌لیتر")
  description?: string; // توضیحات
  value?: string; // مقدار حجم/سایز
  features?: string[]; // ویژگی‌های این حجم/سایز
  color?: string; // رنگ برای نمایش
  icon?: string; // آیکون
}

export interface CreateAccountTypeDto {
  name: string;
  description?: string;
  image?: string;
  category?: string;
  categoryIds?: string[];
  activityArea?: string;
  isActive?: boolean;
  fields: FieldDefinition[];
}

export interface UpdateAccountTypeDto {
  name?: string;
  description?: string;
  image?: string;
  category?: string;
  categoryIds?: string[];
  activityArea?: string;
  isActive?: boolean;
  fields?: FieldDefinition[];
}

const accountTypeService = {
  /**
   * Get a paginated list of account types
   */
  getAccountTypes: async (
    page: number = 1,
    limit: number = 10,
    search?: string,
    isActive?: boolean,
    categoryId?: string
  ): Promise<PaginatedResponse<AccountType>> => {
    try {
      const params: Record<string, any> = {
        page,
        limit,
      };

      if (search) params.search = search;
      if (isActive !== undefined) params.isActive = isActive;
      if (categoryId) params.categoryId = categoryId;

      const response = await apiClient.getPaginated<AccountType>('/admin/account-types', params);

      return {
        data: response.items,
        pagination: {
          total: response.meta.totalItems,
          page: response.meta.currentPage,
          limit: response.meta.itemsPerPage,
          pages: response.meta.totalPages,
        },
      };
    } catch (error) {
      console.error('Error fetching account types:', error);
      throw error;
    }
  },

  /**
   * Get an account type by ID
   */
  getAccountType: async (id: string): Promise<AccountType> => {
    try {
      const response = await apiClient.get<any>(`/admin/account-types/${id}`);

      // ✅ Backend returns `_id` (Mongo) but our interface uses `id`. Normalize so
      // downstream checks like `!accountType.id` (in AccountTypeManagement) work.
      // Also ensure activityArea is the ID string (it comes back populated in detail).
      const normalized: AccountType = {
        ...(response as any),
        id: (response as any).id || (response as any)._id,
        fields: (response as any).fields || [],
        activityArea:
          typeof (response as any).activityArea === 'object' && (response as any).activityArea !== null
            ? (response as any).activityArea._id || (response as any).activityArea.id
            : (response as any).activityArea,
      } as AccountType;

      return normalized;
    } catch (error) {
      console.error(`Error fetching account type with ID ${id}:`, error);

      // Try direct fetch as last resort
      try {
        const token = localStorage.getItem('accessToken');

        if (!token) {
          throw new Error('No access token available');
        }

        const apiUrl = API_BASE_URL;
        const correctPath = `/admin/account-types/${id}`;

        const response = await fetch(`${apiUrl}${correctPath}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Direct fetch failed with status: ${response.status}`);
        }

        const data = await response.json();

        // Extract data from API response
        const raw = data.status === 'success' && data.data ? data.data : data;

        // ✅ Same normalization as the primary path: _id → id, populated activityArea → id string
        const accountType: AccountType = {
          ...raw,
          id: raw.id || raw._id,
          fields: raw.fields || [],
          activityArea:
            typeof raw.activityArea === 'object' && raw.activityArea !== null
              ? raw.activityArea._id || raw.activityArea.id
              : raw.activityArea,
        } as AccountType;

        return accountType;
      } catch (directFetchError) {
        console.error(`Direct fetch also failed:`, directFetchError);
        throw error; // Throw original error
      }
    }
  },

  /**
   * Create a new account type
   */
  createAccountType: async (accountTypeData: CreateAccountTypeDto): Promise<AccountType> => {
    try {
      // Clean activityArea - convert empty string to undefined
      const cleanData = { ...accountTypeData };
      if (cleanData.activityArea === '' || cleanData.activityArea === null) {
        delete cleanData.activityArea;
      }
      return await apiClient.post<AccountType>('/admin/account-types', cleanData);
    } catch (error) {
      console.error('Error creating account type:', error);
      throw error;
    }
  },

  /**
   * Update an account type
   */
  updateAccountType: async (id: string, accountTypeData: UpdateAccountTypeDto): Promise<AccountType> => {
    try {
      // Recursive field sanitizer — normalizes a field and walks its subFields tree
      const sanitizeField = (field: FieldDefinition): FieldDefinition => {
        const cleanField: any = {
          ...field,
          type: field.type,
          name: field.name,
          label: field.label,
          required: field.required !== undefined ? field.required : false,
          order: field.order !== undefined ? field.order : 0,
        };

        if (cleanField.options) {
          const cleanOptions = { ...cleanField.options };
          if (
            field.type !== 'price' &&
            cleanOptions.priceMatrix &&
            Array.isArray(cleanOptions.priceMatrix) &&
            cleanOptions.priceMatrix.length === 0
          ) {
            delete cleanOptions.priceMatrix;
          }
          cleanField.options = cleanOptions;
        }

        // ✅ Recurse into subFields (nested form)
        if (Array.isArray(field.subFields) && field.subFields.length > 0) {
          cleanField.subFields = field.subFields.map(sanitizeField);
        } else if (
          cleanField.subFields !== undefined &&
          (!Array.isArray(cleanField.subFields) || cleanField.subFields.length === 0)
        ) {
          delete cleanField.subFields;
        }

        return cleanField as FieldDefinition;
      };

      const sanitizedFields = Array.isArray(accountTypeData.fields) ? accountTypeData.fields.map(sanitizeField) : [];

      // Make sure fields array is properly structured
      const sanitizedData: any = {
        ...accountTypeData,
        fields: sanitizedFields,
      };

      // Clean activityArea - convert empty string to undefined
      if (sanitizedData.activityArea === '' || sanitizedData.activityArea === null) {
        delete sanitizedData.activityArea;
      }

      const response = await apiClient.put<AccountType>(`/admin/account-types/${id}`, sanitizedData);
      return response;
    } catch (error) {
      console.error(`Error updating account type with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete an account type
   */
  deleteAccountType: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/admin/account-types/${id}`);
    } catch (error) {
      console.error(`Error deleting account type with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Restore an account type
   */
  restoreAccountType: async (id: string): Promise<AccountType> => {
    try {
      return await apiClient.post<AccountType>(`/admin/account-types/${id}/restore`);
    } catch (error) {
      console.error(`Error restoring account type with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get all account type categories
   */
  getCategories: async (): Promise<string[]> => {
    try {
      const result = await apiClient.get<string[]>('/admin/account-types/categories');
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error fetching account type categories:', error);
      return [];
    }
  },

  /**
   * Create a new category
   */
  createCategory: async (name: string): Promise<string[]> => {
    try {
      const response = await apiClient.post<{ data: string[] }>('/admin/account-types/categories', { name });
      return (response as any).data || [];
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  /**
   * Rename a category (cascades to all AccountTypes using it)
   */
  renameCategory: async (oldName: string, newName: string): Promise<string[]> => {
    try {
      const result = await apiClient.put<string[]>(`/admin/account-types/categories/${encodeURIComponent(oldName)}`, {
        newName,
      });
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error renaming category:', error);
      throw error;
    }
  },

  /**
   * Delete a category
   */
  deleteCategory: async (name: string): Promise<string[]> => {
    try {
      const response = await apiClient.delete<{ data: string[] }>(
        `/admin/account-types/categories/${encodeURIComponent(name)}`
      );
      return (response as any).data || [];
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  /**
   * Toggle account type status
   */
  toggleAccountTypeStatus: async (id: string): Promise<AccountType> => {
    try {
      return await apiClient.patch<AccountType>(`/admin/account-types/${id}/status`);
    } catch (error) {
      console.error(`Error toggling status for account type with ID ${id}:`, error);
      throw error;
    }
  },
};

export default accountTypeService;
